import * as THREE from 'three';
import { CARD_WIDTH, CARD_HEIGHT, CARD_THICKNESS } from '../cardMesh';

// ── Burrowing. Static earth rim ─────────────────────────────────
//
// Visually conveys "this unit can burrow underground." The effect
// is deliberately STATIC. Earth doesn't shimmer or animate, it
// just IS. Because the keyword is a passive ability, not an event.
// Continuous animation here would read as a different kind of magic.
//
// Two states:
//
//   1. PASSIVE (keyword present, card on the surface): a thin
//      gritty earth rim hugs the card silhouette. Hint of "this
//      card has the burrowing ability" without being intrusive.
//
//   2. BURROWED (card.subsurface === true): the earth band thickens
//      dramatically and pulls inward, visually swallowing more of
//      the card art. Reads as the card being partially buried.
//      The transition lerps smoothly over ~0.4s so the state change
//      reads as movement rather than a hard pop.
//
// Composition:
//   - Single static plane just above the card surface.
//   - Card SDF defines where the rim sits (band along the inside
//     of the silhouette).
//   - Noise-warped band boundary so the dirt edge is irregular,
//     not a clean offset of the card outline.
//   - Multi-octave noise drives dirt texture: broad clumps + fine
//     grain + sharp dark pebbles for visual variety.
//   - `uBurrowedAmount` uniform scales band thickness from PASSIVE
//     to BURROWED values; updated from `card.subsurface` each
//     frame with smooth interpolation.

const RISE_HEIGHT_OFFSET = 0.04;
const PLANE_W = CARD_WIDTH * 1.1;
const PLANE_H = CARD_HEIGHT * 1.1;

// How fast the burrowed-state transition runs. Higher = snappier;
// lower = more gradual.
const BURROWED_LERP_SPEED = 4.5;

const VERT_SHADER = /* glsl */ `
  varying vec2 vLocalXY;

  void main() {
    vLocalXY = position.xy;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAG_SHADER = /* glsl */ `
  precision highp float;

  uniform vec2  uCardSize;
  uniform float uBurrowedAmount;     // 0 (passive) → 1 (burrowed)
  uniform float uPassiveBandWidth;   // inward band thickness, passive
  uniform float uBurrowedBandWidth;  // inward band thickness, burrowed
  uniform vec3  uColorDark;
  uniform vec3  uColorMid;
  uniform vec3  uColorLight;
  uniform vec3  uColorPebble;

  varying vec2 vLocalXY;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(hash(i),                hash(i + vec2(1.0, 0.0)), u.x),
      mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
      u.y
    );
  }
  float fbm(vec2 p) {
    float sum = 0.0;
    float amp = 0.5;
    for (int i = 0; i < 4; i++) {
      sum += amp * vnoise(p);
      p = p * 2.05 + vec2(1.7, 9.2);
      amp *= 0.55;
    }
    return sum;
  }

  // Voronoi returning F1 distance, F2 distance, AND the vector from
  // the pixel to the nearest cell centre. The delta vector lets us
  // fake a per-stone surface normal:
  //   - At the cell centre, delta = 0 → flat top of the rock.
  //   - As you walk toward the seam, delta grows → surface tilts
  //     outward like the side of a pebble.
  // We use that delta as a 2D screen-space normal and dot it with a
  // fixed light direction to get crisp per-stone highlights and
  // shadows. Standard technique behind Sebastian Lague / Inigo
  // Quilez voronoi-rock shaders.
  vec4 voronoiRock(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    float f1 = 8.0, f2 = 8.0;
    vec2 delta = vec2(0.0);
    for (int y = -1; y <= 1; y++) {
      for (int x = -1; x <= 1; x++) {
        vec2 g = vec2(float(x), float(y));
        vec2 o = vec2(hash(i + g), hash(i + g + vec2(13.1, 7.7)));
        vec2 r = g + o - f;
        float d = length(r);
        if (d < f1) { f2 = f1; f1 = d; delta = -r; }
        else if (d < f2) { f2 = d; }
      }
    }
    return vec4(f1, f2, delta);
  }

  void main() {
    vec2 p = vLocalXY;

    // Card SDF. Negative inside, 0 at edge, positive outside.
    vec2 q = abs(p) - uCardSize * 0.5;
    float cardSdf = length(max(q, vec2(0.0))) + min(max(q.x, q.y), 0.0);

    if (cardSdf > 0.35) discard;
    float insideDist = -cardSdf;

    // ── Jagged silhouette warp ──
    // Two-octave warp pushes the effective "distance from edge" up
    // and down so the rim reads as torn rock, not a clean inset.
    // Amplitude is intentionally small (~0.4). Bigger values cause
    // the band to spill toward the centre.
    float warpLow  = vnoise(p * 0.55);
    float warpHigh = vnoise(p * 1.8 + vec2(5.3, 2.1));
    float warpN = warpLow * 0.65 + warpHigh * 0.35;
    float warpedDist = insideDist + (warpN - 0.5) * 0.42;

    // ── Rim band (narrow frame) ──
    // bandWidth stays tight. Rim only. And the falloff is a
    // smoothstep with full opacity from 0 to ~30% in, then a fast
    // fade out. The centre of the card is always clean.
    float bandWidth = mix(uPassiveBandWidth, uBurrowedBandWidth, uBurrowedAmount);
    float bandT = clamp(warpedDist / bandWidth, 0.0, 1.0);
    float innerBand = 1.0 - smoothstep(0.35, 1.0, bandT);
    // Hard inside cutoff. Anything past the band is gone.
    if (warpedDist > bandWidth + 0.05) { innerBand = 0.0; }

    // Outer overhang: rocks crumble a hair past the card edge.
    float outerBand = smoothstep(0.35, -0.05, cardSdf) * 0.85;
    // Combine: outer kept distinct so it can't bleed inward.
    float bandMask = max(innerBand, outerBand * step(insideDist, 0.15));
    if (bandMask < 0.01) discard;

    // ── Rocky texture with fake per-stone lighting ──
    // Two voronoi scales mixed: big boulders + gravel.
    vec4 vorA = voronoiRock(p * 1.15 + vec2(2.1, 4.7));
    vec4 vorB = voronoiRock(p * 2.30 + vec2(8.3, 1.4));

    // Fake surface normal: delta points from pixel toward cell
    // centre. Magnitude grows toward the seam → side of the rock.
    // dot with a fixed top-left light gives highlight/shadow.
    vec2 lightDir = normalize(vec2(-0.65, 0.75));
    float litA = dot(normalize(vorA.zw + vec2(1e-4)), lightDir) * length(vorA.zw);
    float litB = dot(normalize(vorB.zw + vec2(1e-4)), lightDir) * length(vorB.zw);
    // Blend the two scales: big rocks dominate, small adds detail.
    float lit = litA * 0.75 + litB * 0.25;

    // Crack mask: F2-F1 is small in the seams between stones.
    float crackA = 1.0 - smoothstep(0.02, 0.12, vorA.y - vorA.x);
    float crackB = 1.0 - smoothstep(0.02, 0.10, vorB.y - vorB.x);
    float crack = max(crackA, crackB * 0.6);

    // Per-stone albedo variation: each cell gets a random tint so
    // adjacent rocks don't all look identical. Hash by the seed of
    // the nearest cell. Derive a stable seed from delta direction.
    float cellHash = hash(floor(p * 1.15 - vorA.zw));
    float albedoVar = mix(0.85, 1.15, cellHash);

    // Underlying dirt/grit for the gaps between rocks.
    float dirt = fbm(p * 0.7);
    float grit = vnoise(p * 5.5);

    // ── Colour ──
    // Base earth gradient driven by dirt + grit.
    float baseT = dirt * 0.65 + grit * 0.35;
    vec3 col = mix(uColorDark, uColorMid, smoothstep(0.20, 0.55, baseT));
    col = mix(col, uColorLight, smoothstep(0.55, 0.85, baseT));

    // Tint per stone (warmer or cooler shade per cell).
    col *= albedoVar;

    // Apply directional lighting. Clamp shadow side, lift lit side.
    col *= clamp(1.0 + lit * 1.6, 0.55, 1.55);

    // Crack lines: deep dark between stones.
    col = mix(col, uColorPebble, crack * 0.9);

    // Subtle darkening when burrowed.
    col *= mix(1.0, 0.82, uBurrowedAmount);

    // ── Alpha ──
    // Inner band uses its falloff. Crack/seam lines slightly boost
    // alpha so they read crisp even at low band intensity.
    float baseAlpha = 0.95;
    float alpha = bandMask * baseAlpha;
    alpha = min(1.0, alpha + crack * bandMask * 0.15);

    gl_FragColor = vec4(col, alpha);
  }
`;

export function createBurrowingEarthEffect() {
  const group = new THREE.Group();
  group.frustumCulled = false;

  const geometry = new THREE.PlaneGeometry(PLANE_W, PLANE_H);
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uCardSize: { value: new THREE.Vector2(CARD_WIDTH, CARD_HEIGHT) },
      uBurrowedAmount: { value: 0 },
      // Band widths in world units. Card is ~6.35 × 8.89, so even
      // the burrowed band only occupies the outer ~20% of the half-
      // dimension. Always reads as a frame, never as a wash.
      uPassiveBandWidth: { value: 0.45 },
      uBurrowedBandWidth: { value: 1.25 },
      // Three-stop earth palette + a deep near-black for pebbles.
      uColorDark:   { value: new THREE.Color(0x1a1208) },
      uColorMid:    { value: new THREE.Color(0x4a3520) },
      uColorLight:  { value: new THREE.Color(0x826347) },
      uColorPebble: { value: new THREE.Color(0x0c0804) },
    },
    vertexShader: VERT_SHADER,
    fragmentShader: FRAG_SHADER,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.NormalBlending,
    side: THREE.DoubleSide,
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.frustumCulled = false;
  mesh.position.z = CARD_THICKNESS / 2 + RISE_HEIGHT_OFFSET;
  mesh.renderOrder = 16;
  group.add(mesh);

  // Track the current interpolated burrowed amount on the effect so
  // it persists across update calls without recomputing.
  return {
    points: group,
    materials: [material],
    geometry,
    // Internal state for the smooth state-transition lerp.
    _burrowedAmount: 0,
  };
}

const _tmpVec3 = new THREE.Vector3();
const _tmpEuler = new THREE.Euler();
export function updateBurrowingEarthEffect(effect, dt, cardMesh) {
  if (!effect || !cardMesh) return;

  // Read the burrowed state from the card instance and lerp the
  // visual amount toward it. `subsurface` is the existing flag set
  // when the player double-clicks → activates burrow in the
  // game-logic layer.
  const card = cardMesh.userData?.cardInstance;
  const target = card?.subsurface ? 1 : 0;
  const current = effect._burrowedAmount ?? 0;
  // Exponential approach lerp. Smooth, frame-rate independent.
  const next = current + (target - current) * Math.min(1, dt * BURROWED_LERP_SPEED);
  effect._burrowedAmount = next;
  effect.materials[0].uniforms.uBurrowedAmount.value = next;

  cardMesh.getWorldPosition(_tmpVec3);
  effect.points.position.copy(_tmpVec3);

  const intent = cardMesh.userData?.intentEuler;
  const yaw = intent ? intent.z : 0;
  _tmpEuler.set(-Math.PI / 2, 0, yaw, 'XYZ');
  effect.points.rotation.copy(_tmpEuler);
}

export function disposeBurrowingEarthEffect(effect) {
  if (!effect) return;
  effect.points.parent?.remove(effect.points);
  effect.geometry?.dispose();
  for (const material of effect.materials || []) {
    material.dispose();
  }
}
